import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { UnidataTable, Table } from '~/components';
import { LUBRIFICACAO } from '~/constants/modules';
import { t, can } from '~/helpers';
import { getToastErrors } from '~/helpers/errorsRequest';
import { handleExportGeneric } from '~/helpers/exportReports';
import api from '~/services/api';
import {
  deleteVeiculo,
  findVeiculo,
  recordFiltering,
} from '~/store/modules/locomotiva/actions';

import Form from './form';
import * as S from './styles';

function Locomotiva() {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState({});

  const redux = useSelector((state) => state.locomotiva);

  const status = useMemo(
    () => [
      { label: 'N', value: t('s.geral.opcoes.nao') },
      { label: 'S', value: t('s.geral.opcoes.sim') },
    ],
    []
  );

  const statusDvec = useMemo(
    () => [
      { label: '0', value: t('s.geral.opcoes.nao') },
      { label: '1', value: t('s.geral.opcoes.sim') },
    ],
    []
  );

  const situation = useMemo(
    () => [
      { label: 'L', value: t('s.geral.opcoes.liberado') },
      { label: 'B', value: t('s.geral.opcoes.bloqueado') },
      { label: 'C', value: t('s.geral.opcoes.cancelado') },
    ],
    []
  );

  const tipoequipamento = useMemo(
    () => [
      { label: 'L', value: t('s.geral.opcoes.locomotiva') },
      { label: 'O', value: t('s.geral.opcoes.outros') },
    ],
    []
  );

  const header = useMemo(() => {
    const data = [
      {
        id: 'idveiculo',
        label: t('s.cadastro.veiculo.locomotiva'),
        filter: { type: 'text' },
      },
      {
        id: 'nomeempresaveiculo',
        label: t('s.cadastro.veiculo.nomeempresaveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'nomemodeloveiculo',
        label: t('s.cadastro.veiculo.idmodeloveiculo'),
        filter: { type: 'text' },
      },
      {
        id: 'possuidlocveiculo',
        label: t('s.cadastro.veiculo.possuidlocveiculo'),
        formatted: (value) => status.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: status },
      },
      {
        id: 'possuidvecveiculo',
        label: t('s.cadastro.veiculo.possuia2vf'),
        formatted: (value) =>
          statusDvec.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: statusDvec },
      },
      {
        id: 'possuittagveiculo',
        label: t('s.cadastro.veiculo.possuittagveiculo'),
        formatted: (value) => status.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: status },
      },
      {
        id: 'possuioverfillveiculo',
        label: t('s.cadastro.veiculo.possuioverfillveiculo'),
        formatted: (value) => status.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: status },
      },
      {
        id: 'tipolocomotivaveiculo',
        label: t('s.cadastro.veiculo.tipolocomotivaveiculo'),
        formatted: (value) =>
          tipoequipamento.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: tipoequipamento },
      },
      {
        id: 'statusveiculo',
        label: t('s.cadastro.veiculo.statusveiculo'),
        formatted: (value) =>
          situation.find((s) => s.label === value).value || '',
        filter: { type: 'select', options: situation },
      },
    ];

    return data;
  }, [status, situation, statusDvec]);

  const cellsStock = useMemo(
    () => [
      { id: 'codigonomeposto', label: t('s.relatorio.geral.postocomboio') },
    ],
    []
  );

  const cellsCompartiment = useMemo(
    () => [
      {
        id: 'idcompartimento',
        label: t('s.relatorio.abastecimento.idcompartimento'),
      },
      {
        id: 'descricaocompartimento',
        label: t('s.geral.camposistema.descricaocompartimentostr'),
      },
      {
        id: 'nomeabreviadoproduto',
        label: t('s.geral.camposistema.nomeproduto'),
      },
    ],
    []
  );

  const handleResponseDataItems = useCallback(async (id) => {
    try {
      if (id) {
        setLoading(true);

        await api
          .get(`veiculos/detalhes/${id}`)
          .then((response) => setItems(response.data));
      }
    } catch (err) {
      getToastErrors(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (redux.loading) {
      setItems({});
    }
  }, [redux.loading]);

  const fieldlist = [
    'idveiculo',
    'nomeempresaveiculo',
    'nomemodeloveiculo',
    'possuidlocveiculo',
    'possuidvecveiculo',
    'possuittagveiculo',
    'possuioverfillveiculo',
    'tipolocomotivaveiculo',
    'maxtagveiculo',
    'bitolaveiculo',
    'vazaomaximaveiculo',
    'observacaoveiculo',
    'statusveiculo',
  ];

  const substituirHeader = {
    idveiculo: t('s.cadastro.veiculo.locomotiva'),
    nomeempresaveiculo: t('s.cadastro.veiculo.nomeempresaveiculo'),
    nomemodeloveiculo: t('s.cadastro.veiculo.idmodeloveiculo'),
    possuidlocveiculo: t('s.cadastro.veiculo.possuidlocveiculo'),
    possuidvecveiculo: t('s.cadastro.veiculo.possuia2vf'),
    possuittagveiculo: t('s.cadastro.veiculo.possuittagveiculo'),
    possuioverfillveiculo: t('s.cadastro.veiculo.possuioverfillveiculo'),
    tipolocomotivaveiculo: t('s.cadastro.veiculo.tipolocomotivaveiculo'),
    statusveiculo: t('s.cadastro.veiculo.statusveiculo'),
    maxtagveiculo: t('s.cadastro.veiculo.maxtagveiculo'),
    bitolaveiculo: t('s.cadastro.veiculo.bitolaveiculo'),
    vazaomaximaveiculo: t('s.operacao.abastecimento.vazaomaximamovimentacao'),
    observacaoveiculo: t('s.operacao.limpezatag.observacaohistoricotag'),
  };

  return (
    <S.Container>
      <UnidataTable
        fixed
        redux={redux}
        header={header}
        read={recordFiltering}
        find={findVeiculo}
        remove={deleteVeiculo}
        onRow={(value) => handleResponseDataItems(value.idveiculo)}
        form={Form}
        keys="idveiculo"
        detach="nomeveiculo"
        maxHeight="60vh"
        pdf
        excel
        onExport={(formato) =>
          handleExportGeneric({
            endpoint: 'veiculos/locomotivas',
            formato,
            fields: fieldlist,
            headers: substituirHeader,
            redux,
            ordination: redux.params.ordination,
            orderfield: redux.params.orderfield,
            title: 'Relatorio de Locomotivas',
            substituirValores: {
              statusveiculo: {
                L: t('s.geral.opcoes.liberado'),
                B: t('s.geral.opcoes.bloqueado'),
                C: t('s.geral.opcoes.cancelado'),
              },
              possuidlocveiculo: {
                S: t('s.geral.opcoes.sim'),
                N: t('s.geral.opcoes.nao'),
              },
              possuidvecveiculo: {
                '1': t('s.geral.opcoes.sim'),
                '0': t('s.geral.opcoes.nao'),
              },
              possuittagveiculo: {
                S: t('s.geral.opcoes.sim'),
                N: t('s.geral.opcoes.nao'),
              },
              possuioverfillveiculo: {
                S: t('s.geral.opcoes.sim'),
                N: t('s.geral.opcoes.nao'),
              },
              tipolocomotivaveiculo: {
                L: t('s.geral.opcoes.locomotiva'),
                O: t('s.geral.opcoes.outros'),
              },
              bitolaveiculo: {
                L: t('s.cadastro.veiculo.bitolaveiculo.largo'),
                E: t('s.cadastro.veiculo.bitolaveiculo.estreito'),
              },
            },
          })
        }
      />

      <S.Wrapper grids="2">
        <Table
          isResponsive
          size="small"
          height="18rem"
          paginate={99}
          showPagination={false}
          cells={cellsStock}
          data={items.postoscomboios ?? []}
          loding={loading}
        />

        {can(LUBRIFICACAO) && (
          <Table
            isResponsive
            size="small"
            height="18rem"
            paginate={99}
            showPagination={false}
            cells={cellsCompartiment}
            data={items.compartimentos ?? []}
            loding={loading}
          />
        )}
      </S.Wrapper>
    </S.Container>
  );
}

export default Locomotiva;
